import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/**
 * Validates file size against MIME type restrictions
 * @param {File} file - The file to validate
 * @param {Object} maxFileSize - Configuration object with MIME type patterns and size limits in MB
 * @returns {Object} Validation result with { isValid: boolean, errorMessage?: string }
 */
const validateFileSize = (file, maxFileSize) => {
  if (!maxFileSize || Object.keys(maxFileSize).length === 0) {
    return {
      isValid: true
    };
  }
  const fileMimeType = file.type;
  const fileSizeInMB = file.size / (1024 * 1024); // Convert bytes to MB

  // If file type is undefined/null, reject the file as it may be corrupted or dangerous
  if (!fileMimeType) {
    return {
      isValid: false,
      errorMessage: 'File MIME type is undefined. Upload cannot be completed.'
    };
  }

  // Find matching MIME type pattern
  let maxSizeMB = null;
  let matchedPattern = null;
  Object.entries(maxFileSize).some(_ref => {
    let _ref2 = _slicedToArray(_ref, 2),
      mimePattern = _ref2[0],
      sizeLimit = _ref2[1];
    if (mimePattern === fileMimeType) {
      // Exact match
      maxSizeMB = sizeLimit;
      matchedPattern = mimePattern;
      return true;
    }
    if (mimePattern.endsWith('/*')) {
      // Wildcard match (e.g., 'image/*' matches 'image/png')
      const baseType = mimePattern.slice(0, -1); // Remove the '*'
      if (fileMimeType.startsWith(baseType)) {
        maxSizeMB = sizeLimit;
        matchedPattern = mimePattern;
        return true;
      }
    }
    return false;
  });
  if (maxSizeMB === null) {
    // No matching pattern found, file is allowed
    return {
      isValid: true
    };
  }
  if (fileSizeInMB > maxSizeMB) {
    return {
      isValid: false,
      errorMessage: "File size (".concat(fileSizeInMB.toFixed(2), " MB) exceeds the maximum allowed size ") + "(".concat(maxSizeMB, " MB) for ").concat(matchedPattern, " files.")
    };
  }
  return {
    isValid: true
  };
};

/**
 * Validates multiple files against MIME type restrictions and provides comprehensive feedback
 * @param {File[]} files - Array of files to validate
 * @param {Object} maxFileSize - Configuration object with MIME type patterns and size limits in MB
 * @returns {Object} Validation result with:
 *   {
 *     isValid: boolean,
 *     errorMessage?: string,
 *     violations?: Array
 *   }
 */
const validateMultipleFiles = (files, maxFileSize) => {
  if (!maxFileSize || Object.keys(maxFileSize).length === 0) {
    return {
      isValid: true
    };
  }
  const violations = [];
  files.forEach(file => {
    const validation = validateFileSize(file, maxFileSize);
    if (!validation.isValid) {
      violations.push({
        filename: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2),
        mimeType: file.type,
        errorMessage: validation.errorMessage
      });
    }
  });
  if (violations.length === 0) {
    return {
      isValid: true
    };
  }

  // Create comprehensive error message
  const part1 = 'Upload cannot be completed!\n\n';
  const part2 = 'These files are too large:\n';
  let errorMessage = part1 + part2;

  // Group violations by MIME type to show limits
  const mimeTypeLimits = {};
  violations.forEach(violation => {
    errorMessage += "\u2022 ".concat(violation.filename, " (").concat(violation.size, " MB)\n");

    // Find the max size limit for this MIME type
    let maxSizeMB = null;
    let matchedPattern = null;
    Object.entries(maxFileSize).some(_ref3 => {
      let _ref4 = _slicedToArray(_ref3, 2),
        mimePattern = _ref4[0],
        sizeLimit = _ref4[1];
      if (mimePattern === violation.mimeType) {
        maxSizeMB = sizeLimit;
        matchedPattern = mimePattern;
        return true;
      }
      if (mimePattern.endsWith('/*')) {
        const baseType = mimePattern.slice(0, -1);
        if (violation.mimeType.startsWith(baseType)) {
          maxSizeMB = sizeLimit;
          matchedPattern = mimePattern;
          return true;
        }
      }
      return false;
    });
    if (maxSizeMB !== null) {
      mimeTypeLimits[matchedPattern] = maxSizeMB;
    }
  });

  // Add MIME type limits section
  if (Object.keys(mimeTypeLimits).length > 0) {
    errorMessage += '\nMaximum file sizes:\n';
    Object.entries(mimeTypeLimits).forEach(_ref5 => {
      let _ref6 = _slicedToArray(_ref5, 2),
        mimeType = _ref6[0],
        limit = _ref6[1];
      errorMessage += "\u2022 ".concat(mimeType, ": ").concat(limit, " MB\n");
    });
  }

  // Add instruction
  errorMessage += '\nPlease try again with smaller file(s).';
  return {
    isValid: false,
    errorMessage: errorMessage.trim(),
    violations
  };
};
export default {
  validateFileSize,
  validateMultipleFiles
};